import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PhoneCall, MessageCircle, MessageSquare, Loader2 } from "lucide-react";

const CHANNELS = [
  { id: "call", label: "Voice Call", icon: PhoneCall, testid: "simulate-call-btn", color: "text-blue-400" },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, testid: "simulate-whatsapp-btn", color: "text-emerald-400" },
  { id: "sms", label: "SMS", icon: MessageSquare, testid: "simulate-sms-btn", color: "text-purple-400" },
];

export default function SimulateDrawer({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [channel, setChannel] = useState("call");
  const [name, setName] = useState("Rahim Uddin");
  const [phone, setPhone] = useState("+919800112233");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);

  const fire = async () => {
    setLoading(true);
    try {
      const workspace_id = localStorage.getItem("workspace_id");
      const { data } = await api.post("/simulate/inbound", { workspace_id, channel, phone, name, body });
      toast.success(
        data.after_hours ? "Inbound received — routed to after-hours flow" : "Inbound received — routed to inbox",
        { description: `Ticket auto-created for ${data.contact.name}` }
      );
      onOpenChange(false);
      navigate("/inbox", { state: { focus: data.contact.id, ts: Date.now() } });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-[#05261D] border-l border-gold/20" data-testid="simulate-drawer">
        <SheetHeader>
          <SheetTitle className="font-serif text-gold">Simulate Inbound Event</SheetTitle>
          <SheetDescription>
            Trigger a live inbound call or message to watch the console react. Exotel & WhatsApp go live once credentials are connected.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-5">
          <div>
            <Label className="text-xs text-muted-foreground">Channel</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {CHANNELS.map((c) => {
                const Icon = c.icon;
                const active = channel === c.id;
                return (
                  <button key={c.id} data-testid={c.testid} onClick={() => setChannel(c.id)}
                    className={`flex flex-col items-center gap-1.5 py-3 rounded-lg border text-xs transition-all ${
                      active ? "border-gold bg-gold/10 gold-glow" : "border-border/60 hover:border-gold/40"
                    }`}>
                    <Icon className={`w-5 h-5 ${c.color}`} />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label htmlFor="sim-name" className="text-xs text-muted-foreground">Caller name</Label>
            <Input id="sim-name" data-testid="simulate-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-card/60" />
          </div>
          <div>
            <Label htmlFor="sim-phone" className="text-xs text-muted-foreground">Phone number</Label>
            <Input id="sim-phone" data-testid="simulate-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 bg-card/60" />
          </div>
          {channel !== "call" && (
            <div>
              <Label htmlFor="sim-body" className="text-xs text-muted-foreground">Message</Label>
              <Textarea id="sim-body" data-testid="simulate-body-input" value={body} onChange={(e) => setBody(e.target.value)}
                placeholder="Assalamu Alaikum, I'd like Umrah package details…" className="mt-1.5 bg-card/60" />
            </div>
          )}

          <Button data-testid="simulate-fire-button" onClick={fire} disabled={loading}
            className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Trigger Inbound"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
